import { createSlice } from '@reduxjs/toolkit'
import name from './name'
import { ACCELERATE_STATUS } from '../../consts'

const initialState = {
  isLoading: true,
  hasError: false,
  status: ACCELERATE_STATUS.ASSESSMENT_PENDING,
  progress: null,
  mastery: null,
  momentum: null,
  assessmentProgress: null,
  recommendations: [],
  report: null,
  journeySteps: null,
  badges: null,
  dismissedBadgeIds: [],
  hasApplySkillsInteraction: false,
  scormCourseId: '',
  // Licence check for navigation and route guards (accelerateGetAccess), kept
  // apart from the status: the status builds the whole SA page and is slow.
  access: { hasAccess: false, isLoading: true, hasError: false }
}

export const accelerate = createSlice({
  name,
  initialState,
  reducers: {
    setStatus: (state, { payload }) => {
      state.status = payload.status
      state.isLoading = false
      state.hasError = false

      if (payload?.progress) {
        state.progress = payload.progress
      }

      if (payload?.journeySteps) {
        state.journeySteps = payload.journeySteps
      }

      // Store assessment progress for in-progress assessments
      if (payload?.assessmentProgress) {
        state.assessmentProgress = payload.assessmentProgress
      } else {
        state.assessmentProgress = null
      }

      // Store momentum data
      if (payload?.momentum) {
        state.momentum = payload.momentum
      }

      // Store mastery data when returned with status (for completed journeys)
      if (payload?.mastery) {
        state.mastery = payload.mastery
      }

      // Store report data when returned with status (avoids separate getAssessmentReport call)
      if (payload?.report) {
        state.report = payload.report
      }

      // Store Apply Your Skills interaction flag
      if (payload?.hasApplySkillsInteraction !== undefined) {
        state.hasApplySkillsInteraction = payload.hasApplySkillsInteraction
      }

      // Store the SCORM course id (set when the assessment is SCORM-backed) so
      // the launch CTA can route to SCORM instead of Brilliant.
      if (payload?.scormCourseId !== undefined) {
        state.scormCourseId = payload.scormCourseId || ''
      }

      // Store badges data
      if (payload?.badges) {
        state.badges = payload.badges
      } else if (state.badges) {
        // Clear newlyEarned to prevent stale modal triggers when
        // backend response doesn't include badges (e.g. early return paths)
        state.badges = { ...state.badges, newlyEarned: [] }
      }
    },
    setReport: (state, { payload }) => {
      state.report = payload
    },
    setRecommendations: (state, { payload }) => {
      state.recommendations = payload
    },
    setProgress: (state, { payload }) => {
      state.status = payload.status

      if (payload?.progress) {
        state.progress = payload.progress
      }

      // Store mastery data when journey is complete
      if (payload?.mastery) {
        state.mastery = payload.mastery
      }

      // Store Apply Your Skills interaction flag
      if (payload?.hasApplySkillsInteraction !== undefined) {
        state.hasApplySkillsInteraction = payload.hasApplySkillsInteraction
      }

      // Store badges data
      if (payload?.badges) {
        state.badges = payload.badges
      } else if (state.badges) {
        state.badges = { ...state.badges, newlyEarned: [] }
      }
    },
    setBadges: (state, { payload }) => {
      state.badges = payload
      // When badges are set from trackApplySkillsInteraction, mark the interaction as complete
      const hasJourneyMaster = payload?.earned?.some(b => b.id === 'journey-master')
      if (hasJourneyMaster) {
        state.hasApplySkillsInteraction = true
      }
    },
    dismissBadges: (state, { payload: badgeIds }) => {
      const existing = new Set(state.dismissedBadgeIds)
      badgeIds.forEach(id => existing.add(id))
      state.dismissedBadgeIds = [...existing]
    },
    setLoadingError: (state) => {
      state.isLoading = false
      state.hasError = true
    },
    setAccess: (state, { payload }) => {
      state.access = { hasAccess: payload?.hasAccess === true, isLoading: false, hasError: false }
    },
    setAccessError: (state) => {
      state.access = { hasAccess: false, isLoading: false, hasError: true }
    },
    resetState: () => initialState
  }
})

export const { setStatus, setReport, setRecommendations, setProgress, setBadges, dismissBadges, setLoadingError, setAccess, setAccessError, resetState } = accelerate.actions

export default accelerate.reducer
